/**
 * How a problem gets credited — the rule alone, with no database behind it.
 *
 * Deliberately a leaf: the learner's tRPC procedure reaches it through
 * `problem-sources.ts` (which does import Drizzle), while the admin problem
 * editor's live preview calls it directly on unsaved form state. Neither can
 * be allowed to grow its own copy of "which source is the original", and a
 * client bundle must not pull `db/` in to ask.
 */

/**
 * `Source.slug` of the house source. Problems it owns are BCTCI's own content:
 * they need no attribution of their own, and they are the exception to the
 * client-only gate on imported material.
 */
export const BCTCI_SOURCE_SLUG = 'beyond-cracking-the-coding-interview'

/** The `ProblemSource.id` prefix marking a synthesized owning-source row. */
export const OWNING_SOURCE_ID_PREFIX = 'owning:'

export function isOwningSourceId(id: string): boolean {
  return id.startsWith(OWNING_SOURCE_ID_PREFIX)
}

/**
 * A source in the running to be credited, flattened from whichever side it
 * came from — a joined database row or an admin form's current state.
 */
export interface AttributionCandidate {
  /** `owning:<problemId>` for the synthesized owning row, else its row id. */
  id: string
  /** `Source.slug`, or `null` when the source could not be resolved. */
  sourceSlug: string | null
  /** Display name, already resolved (company-backed sources included). */
  name: string
  /** Raw authored `Source.icon` value; `AuthoredIcon` resolves it. */
  icon: string | null
  /** This source's own title for the problem, if it recorded one. */
  title: string | null
  /** Deep link to the problem on this source. */
  url: string | null
  /** The source's front page, used when there is no deep link. */
  siteUrl: string | null
  premiumRequired: boolean
  /** Whether this row is explicitly marked as where the problem originated. */
  isOriginal: boolean
}

/**
 * One place a problem is credited, as the learner sees it.
 *
 * Deliberately narrow: no ids of admin-only neighbours, no editorial URL, no
 * difficulty. Everything here is printed.
 */
export interface AttributionSource {
  /** Stable React key. */
  id: string
  name: string
  icon: string | null
  /**
   * The title this source published the problem under, and only when it
   * differs from ours — a row repeating BCTCI's own title says nothing, so the
   * comparison is settled here rather than in each renderer.
   */
  publishedAs: string | null
  /**
   * Where to send someone who wants to practise it there: the deep link when
   * there is one, else the site's front page. `null` when neither exists — a
   * book, or a company-backed source with no site — and the row renders
   * unlinked.
   */
  url: string | null
  premiumRequired: boolean
}

/**
 * Whether a copyright notice may be printed over a problem, and how much it
 * may claim — one value because on this surface they are the same question.
 *
 * Structurally the same union as `CopyrightScope` in `components/ui/copyright`,
 * repeated rather than imported so this leaf keeps no dependency on a
 * component — the notice is one renderer of the answer, not its owner.
 */
export type AttributionCopyrightScope = 'all' | 'none'

export interface ProblemAttribution {
  /** The source to credit, or `null` when nothing outranks BCTCI itself. */
  original: AttributionSource | null
  /** Everywhere else the problem can be practised. */
  others: AttributionSource[]
  /**
   * Whether the copyright notice may be printed — decided here, where the
   * owning candidate is still in scope, because `original` alone cannot tell
   * "BCTCI wrote this" apart from "we could not work out who did".
   */
  copyrightScope: AttributionCopyrightScope
}

/** The owning candidate, found by its marker rather than by position. */
export function findOwningCandidate(
  candidates: readonly AttributionCandidate[],
): AttributionCandidate | undefined {
  return candidates.find((candidate) => isOwningSourceId(candidate.id))
}

/**
 * Whether a candidate's source is anybody other than BCTCI.
 *
 * The weaker of the two source tests, and the one the copyright asks — it
 * needs no name to print, so a source we can no longer resolve still counts as
 * somebody else's. No API path can mark a BCTCI row today
 * (`updateProblemSource` rejects it), so this guards data written before that
 * check and anything reaching the table another way.
 */
function isOutsideSource(candidate: AttributionCandidate): boolean {
  return candidate.sourceSlug !== BCTCI_SOURCE_SLUG
}

/**
 * Whether a candidate may be credited as somebody else's original.
 *
 * Being an outside source is necessary but not sufficient: a candidate whose
 * source could not be resolved (`sourceSlug === null`, from a soft-deleted
 * `Source`) is outside, yet would render an ORIGINAL pill reading "Unknown
 * Source" — worse than crediting nobody. So the pill asks for both. Defined
 * through `isOutsideSource` rather than repeating it, because collapsing the
 * two is exactly what reopens the overclaim.
 */
export function isCreditable(candidate: AttributionCandidate): boolean {
  return candidate.sourceSlug !== null && isOutsideSource(candidate)
}

/**
 * Who owns the problem — the three states the publish guard can actually meet.
 *
 * A tagged union rather than the `{ ownedByBctci, name }` pair it replaced,
 * because that pair admits a fourth combination the rule cannot produce: ours
 * *and* nameable. `name` came from `isCreditable`, which excludes BCTCI, so
 * `ownedByBctci: true` always meant `name: null`. Nothing in the type said so,
 * every consumer had to re-derive it from two independent reads, and `null`
 * was doing double duty for "ours, no outside owner to name" and "we could not
 * resolve the owner at all".
 *
 * `unknown` is the case worth the ceremony: a row synthesized from
 * `Problem.sourceId` whose join to `Source` was filtered by `live()` arrives
 * with `sourceSlug === null` and a name that has fallen back to the literal
 * string "Unknown Source". Printed, that reads as a real company the problem
 * belongs to — so `name` exists only on the arm that may print it.
 */
export type ProblemOwner =
  | { kind: 'bctci' }
  | { kind: 'outside'; name: string }
  | { kind: 'unknown' }

/**
 * The publish guard asks two questions of the owning row and must not answer
 * either by hand: whether making the problem public re-exposes somebody else's
 * content, and whether the warning may say whose. They come from one function
 * because they come from one row.
 *
 * Nameability is the subtler of the two, and is decided by the same
 * `isCreditable` test the widget uses before printing an ORIGINAL pill — so
 * the admin's guard and the learner's credit cannot disagree about who is
 * nameable.
 */
export function resolveOwner(
  candidates: readonly AttributionCandidate[],
): ProblemOwner {
  const owner = findOwningCandidate(candidates)
  // No owning row counts as not-ours: an unrecorded owner is not evidence the
  // problem is BCTCI's, and the rows whose provenance is least defensible are
  // the ones that must not sail past the guard unwarned.
  if (!owner) return { kind: 'unknown' }
  if (!isOutsideSource(owner)) return { kind: 'bctci' }
  return isCreditable(owner)
    ? { kind: 'outside', name: owner.name }
    : { kind: 'unknown' }
}

/**
 * Resolve which candidate is the problem's *original*, per the agreed ladder:
 *
 * 1. the row explicitly marked `isOriginal`;
 * 2. else the owning source, when it is not BCTCI — this is what spares the
 *    imported corpus a backfill, since an imported problem is owned by the
 *    site it came from;
 * 3. else none. A BCTCI-owned problem with nothing marked has no original to
 *    credit, and the widget shows no ORIGINAL pill.
 *
 * Never resolved from array position: rule 1 reads a flag and rule 2 reads the
 * `owning:` marker. Both are filtered through `isCreditable`, so the two rules
 * cannot disagree about who is off-limits.
 */
export function resolveOriginalCandidate(
  candidates: readonly AttributionCandidate[],
): AttributionCandidate | undefined {
  const marked = candidates.find(
    (candidate) => candidate.isOriginal && isCreditable(candidate),
  )
  if (marked) return marked

  const owning = findOwningCandidate(candidates)
  if (owning && isCreditable(owning)) {
    return owning
  }

  return undefined
}

function toAttributionSource(
  candidate: AttributionCandidate,
  problemTitle: string,
): AttributionSource {
  const { title } = candidate
  return {
    id: candidate.id,
    name: candidate.name,
    icon: candidate.icon,
    publishedAs: title && title !== problemTitle ? title : null,
    // `||`, not `??`: an empty-string `url` is reachable — the import scripts
    // persist `src.url ?? null` and pass `''` straight through, unlike the
    // admin mutations, which `.trim() || null`. With `??` that empty string
    // beats a perfectly good `siteUrl`, and the row renders with no link at
    // all, which is the one outcome the fallback exists to prevent.
    url: candidate.url || candidate.siteUrl || null,
    premiumRequired: candidate.premiumRequired,
  }
}

/**
 * Split candidates into the source to credit and the rest.
 *
 * BCTCI's own owning row is dropped rather than listed under "also available
 * on" — the widget renders inside BCTCI, so naming ourselves as somewhere else
 * to find the problem is noise. So is a row whose `Source` could not be
 * resolved: it would render as "Unknown Source", crediting nobody and linking
 * nowhere. Every other candidate survives, including a non-BCTCI owning row
 * that lost the ORIGINAL slot to an explicitly marked one — it is still a
 * truthful place to practise.
 *
 * Input order is preserved, so the caller's stable ordering carries through.
 */
export function buildAttribution(
  candidates: readonly AttributionCandidate[],
  problemTitle: string,
): ProblemAttribution {
  const originalCandidate = resolveOriginalCandidate(candidates)
  const owning = findOwningCandidate(candidates)

  // Whether the data says somebody else posed the question. Deliberately a
  // weaker test than `isCreditable`, and deliberately not
  // `originalCandidate !== undefined`: that ladder answers "may we render an
  // ORIGINAL pill", which additionally needs a resolvable `Source` to name and
  // link. This asks whether we may claim the question as ours, and a mark
  // whose `Source` row was soft-deleted still says we may not. An admin
  // de-duping a "Leetcode"/"LeetCode" pair must not thereby hand us the
  // copyright to every problem whose origin pointed at the losing row.
  const markedElsewhere = candidates.some(
    (candidate) => candidate.isOriginal && isOutsideSource(candidate),
  )

  const others = candidates.filter(
    (candidate) =>
      candidate !== originalCandidate &&
      candidate.sourceSlug !== null &&
      // Only the *owning* BCTCI row is dropped — that one says "you are looking
      // at it". A separate BCTCI `ProblemSource` row is a real second place to
      // find the problem: our own book, which renders unlinked. Keeping it is
      // deliberate, so this test is narrower than `isCreditable`'s.
      !(candidate === owning && candidate.sourceSlug === BCTCI_SOURCE_SLUG),
  )

  return {
    original: originalCandidate
      ? toAttributionSource(originalCandidate, problemTitle)
      : null,
    others: others.map((candidate) =>
      toAttributionSource(candidate, problemTitle),
    ),
    // Printing a notice requires *knowing* the problem is ours twice over:
    // ours to own, which only an owning row resolving to BCTCI establishes,
    // and ours to *pose*. The two come apart — BCTCI can host a problem
    // LeetCode asked first, and then the widget credits LeetCode while a
    // blanket claim printed beside it says the question is ours.
    //
    // Anything short of both is `none`: no notice at all, rather than a
    // narrower claim over the statement and test cases. A partial claim has to
    // assert our wording is original, which is a fight to lose later if a
    // statement reads too close to the one it was written from (Mike, LG-409).
    //
    // A problem with no owning row, or one whose `Source` was soft-deleted, is
    // not thereby ours either. Reading the absence of an original as ownership
    // would print "all rights reserved" over precisely the problems whose
    // provenance is least defensible.
    copyrightScope:
      owning?.sourceSlug === BCTCI_SOURCE_SLUG && !markedElsewhere
        ? 'all'
        : 'none',
  }
}

/** Whether there is anything at all to render for this problem. */
export function hasAttribution(attribution: ProblemAttribution): boolean {
  return attribution.original !== null || attribution.others.length > 0
}

/**
 * Whether a problem's copyright notice may be printed — the same rule for the
 * learner's panel and the admin editor's preview, which would otherwise each
 * spell it out and drift.
 *
 * `null` means the answer is not known: still loading, the query failed, or a
 * caller deliberately never asked (an interview candidate, who must not learn
 * where the problem came from). All three resolve to `none`, because the two
 * errors are not symmetric — claiming everything over a problem that
 * originated elsewhere is the overclaim LG-409 exists to stop, while staying
 * silent over a problem we did originate forfeits nothing we were relying on.
 *
 * The cost of resolving the unknown case this way is that the notice appears
 * on a BCTCI problem only once the query lands, rather than on first paint. A
 * notice that arrives a moment late is the cheaper of the two failures.
 */
export function copyrightScopeFor(
  attribution: ProblemAttribution | null,
): AttributionCopyrightScope {
  return attribution?.copyrightScope ?? 'none'
}
